import { useState, type FormEvent, type ReactElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Lock } from 'lucide-react';
import { useLogin } from '@/lib/auth';
import { RateLimitedError, UnauthorizedError } from '@/lib/api';

/**
 * M3.0 — LoginForm premium dark.
 *
 * Misma lógica del form anterior: passphrase, errores 401/429, redirección
 * la decide el root route. Solo cambia la presentación: dark + magma CTA +
 * iconografía Lucide + micro-interacciones del input.
 */
export function LoginForm(): ReactElement {
  const [passphrase, setPassphrase] = useState('');
  const login = useLogin();

  const errorId = 'login-error';

  function getErrorMessage(): string | null {
    const err = login.error;
    if (!err) return null;
    if (err instanceof UnauthorizedError) {
      return 'Passphrase incorrecta.';
    }
    if (err instanceof RateLimitedError) {
      const seconds = err.retryAfterSeconds;
      if (seconds === null) {
        return 'Demasiados intentos. Inténtalo de nuevo en unos minutos.';
      }
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Demasiados intentos. Espera ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
    }
    return 'No se pudo contactar con el backend. Asegúrate de que el servicio está corriendo y recarga la página.';
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!passphrase || login.isPending) return;
    login.mutate(passphrase);
  }

  const errorMessage = getErrorMessage();

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div>
        <p className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-magma-500">
          Bienvenido
        </p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-mist-50">
          Inicia sesión
        </h2>
        <p className="mt-2 text-sm text-mist-100">
          Introduce tu passphrase para acceder.
        </p>
      </div>

      <div>
        <label
          htmlFor="passphrase"
          className="block text-xs font-medium uppercase tracking-wider text-mist-200"
        >
          Passphrase
        </label>
        <div className="relative mt-2">
          <Lock
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist-300"
            aria-hidden="true"
          />
          <input
            id="passphrase"
            name="passphrase"
            type="password"
            autoComplete="current-password"
            required
            autoFocus
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            aria-invalid={errorMessage !== null}
            aria-describedby={errorMessage ? errorId : undefined}
            disabled={login.isPending}
            className="block w-full rounded-md border border-white/10 bg-carbon-900/60 px-3 py-2.5 pl-10 text-mist-50 placeholder-mist-300 shadow-inner backdrop-blur-sm transition-all focus:border-magma-500/50 focus:outline-none focus:ring-2 focus:ring-magma-500/30 disabled:cursor-not-allowed disabled:opacity-60"
            placeholder="••••••••••••"
          />
        </div>
      </div>

      {errorMessage && (
        <motion.p
          id={errorId}
          role="alert"
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-md border border-red-500/20 bg-red-500/10 px-3 py-2.5 text-sm text-red-300"
        >
          {errorMessage}
        </motion.p>
      )}

      <button
        type="submit"
        disabled={login.isPending || passphrase.length === 0}
        aria-busy={login.isPending}
        className="group btn-magma flex w-full items-center justify-center gap-2"
      >
        <span>{login.isPending ? 'Entrando…' : 'Entrar'}</span>
        {!login.isPending && (
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        )}
      </button>
    </form>
  );
}
